import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Drawer, message } from "antd";
import {
    GetAuditoriaClinicaDetalle,
    GetAuditoriaClinicaHistorial,
    GetAuditoriaClinicaComentario,
    PostAuditoriaClinicaComentario,
    PostAuditoriaClinicaCorreccion,
    PostIniciarAuditoria,
    PostMarcarAuditada,
} from "../../Service/Service.auditoriaClinica";
import AuditoriaHistorialCorrecciones from "../../Components/AuditoriaClinica/AuditoriaHistorialCorrecciones";
import AtencionComentarioGeneral from "../../Components/AuditoriaClinica/rediseno/AtencionComentarioGeneral";
import AtencionBarraSuperior from "../../Components/AuditoriaClinica/rediseno/AtencionBarraSuperior";
import AtencionIdentificacion from "../../Components/AuditoriaClinica/rediseno/AtencionIdentificacion";
import AtencionIndiceSecciones from "../../Components/AuditoriaClinica/rediseno/AtencionIndiceSecciones";
import AtencionSeccionCard from "../../Components/AuditoriaClinica/rediseno/AtencionSeccionCard";
import AtencionSinRegistroBloque from "../../Components/AuditoriaClinica/rediseno/AtencionSinRegistroBloque";
import AtencionPanelCorrecciones from "../../Components/AuditoriaClinica/rediseno/AtencionPanelCorrecciones";
import AtencionBarraAcciones from "../../Components/AuditoriaClinica/rediseno/AtencionBarraAcciones";
import AtencionSeccionEdicion from "../../Components/AuditoriaClinica/rediseno/AtencionSeccionEdicion";
import AtencionSeccionPie from "../../Components/AuditoriaClinica/rediseno/AtencionSeccionPie";
import AtencionSkeleton from "../../Components/AuditoriaClinica/rediseno/AtencionSkeleton";
import Modal, { ResumenConsecuencias } from "../../Components/UI/Modal/Modal";
import usePermissions from "../../hook/usePermissions";
import {
    particionarSecciones,
    resumenProgreso,
    seccionesCorregidas,
    ultimaCorreccionDeSeccion,
    cambiosRecientes,
    SECCION_LABEL,
} from "../../utils/auditoriaSecciones";
import { puedeCorregirse, chipElegibilidad, MOTIVO_NO_AUDITABLE } from "../../utils/auditoriaElegibilidad";
import {
    iniciarEdicion,
    construirCambios,
    hayCambioReal,
    motivoNoGuardable,
} from "../../utils/auditoriaCorreccion";
import "../../styles/AuditoriaClinicaRediseno.css";

// AUDITORIA-CLINICA-REDISENO (FASE 1) -- vista de la atención a PANTALLA
// COMPLETA, ruta nueva `/auditoria-clinica/:idcita`.
//
// Sustituye la triple capa tabla -> drawer -> modal por una sola superficie:
// la identificación del paciente y el estado de la cita están SIEMPRE a la
// vista, incluso mientras se corrige (FASE 2). La pantalla actual
// `/auditoria-clinica` (tabla + drawer + modal) sigue existiendo intacta.
//
// Es 100% aditiva: reutiliza los mismos servicios/endpoints ya existentes
// (`GetAuditoriaClinicaDetalle`, `...Historial`, `PostIniciarAuditoria`,
// `PostMarcarAuditada`) y el mismo componente de historial. Cero cambios de
// API, DTO o schema.
// Vuelta por defecto cuando se entra por URL directa (sin `retorno`): la
// bandeja, que es la raíz del módulo. Si se llegó desde una lista concreta,
// `retorno.ruta` manda.
export const RUTA_LISTA_AUDITORIA = "/auditoria-clinica";

const CITA_ESTADO_FINALIZADA = 6;

// Mismo tope que valida el backend (MarcarAuditadaCita). No hay minimo: el
// caso mas frecuente es "OK", y exigir diez caracteres obligaria a rellenar.
// AUD-EDIT-QA-03: subido de 1000 a 5000, ver FAuditClinicalReview.ts.
const COMENTARIO_MAX = 5000;

function AuditoriaAtencionDetalle() {
    const { idcita: idcitaParam } = useParams();
    const idcita = Number(idcitaParam);
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = usePermissions();

    // Contexto que trae la lista: la fila (cobertura/aseguradora/plan, que el
    // detalle no devuelve), la cola de atenciones del conjunto filtrado y los
    // filtros a los que volver. Al entrar por URL directa no existe: la
    // pantalla funciona igual, sólo sin cola ni cobertura.
    const contexto = location.state || {};
    const cola = contexto.cola && Array.isArray(contexto.cola.ids) ? contexto.cola : null;

    const [detalle, setDetalle] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [ejecutando, setEjecutando] = useState(false);
    const [historialAbierto, setHistorialAbierto] = useState(false);
    const [historialKey, setHistorialKey] = useState(0);
    const [seccionActiva, setSeccionActiva] = useState(null);
    const [ocultarSinRegistro, setOcultarSinRegistro] = useState(false);
    // FASE 2: una sola sección en edición a la vez. `null` = nada en curso.
    const [edicion, setEdicion] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [errorEdicion, setErrorEdicion] = useState(null);
    // Cerrar la auditoría es el final del flujo: se confirma mostrando QUÉ se
    // está cerrando, no un "¿Está seguro?" a secas (FASE 4, regla 7).
    const [confirmarCierre, setConfirmarCierre] = useState(false);
    // COMENTARIO-GENERAL: lo que el auditor opina de la atención EN CONJUNTO.
    // Texto libre y opcional, sin mínimo: el caso más frecuente es "OK".
    const [comentarioCierre, setComentarioCierre] = useState("");
    // AUD-EDIT-01: comentario general EDITABLE, independiente del cierre.
    const [comentarioActual, setComentarioActual] = useState(null);
    const [comentarioHistorial, setComentarioHistorial] = useState([]);
    const [guardandoComentario, setGuardandoComentario] = useState(false);
    const [errorComentario, setErrorComentario] = useState(null);
    const contenedorRef = useRef(null);

    const cargar = useCallback(async () => {
        if (!idcita || Number.isNaN(idcita)) {
            setError("El identificador de la atención no es válido.");
            setCargando(false);
            return;
        }
        setCargando(true);
        setError(null);
        const [resDetalle, resHistorial, resComentario] = await Promise.all([
            GetAuditoriaClinicaDetalle(idcita),
            GetAuditoriaClinicaHistorial(idcita),
            GetAuditoriaClinicaComentario(idcita),
        ]);
        if (resDetalle?.success) {
            setDetalle(resDetalle.data);
        } else {
            setDetalle(null);
            setError(resDetalle?.message || "No se pudo obtener el detalle de la atención.");
        }
        // El historial vacío es un resultado VÁLIDO, no un error: no se
        // bloquea la pantalla por él.
        setHistorial(resHistorial?.success ? resHistorial.data || [] : []);
        setComentarioActual(resComentario?.success ? resComentario.data?.actual || null : null);
        setComentarioHistorial(resComentario?.success ? resComentario.data?.historial || [] : []);
        setCargando(false);
    }, [idcita]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    // Identidad estable: sin esto, `secciones` sería un objeto nuevo en cada
    // render y anularía los useMemo que dependen de él.
    const secciones = useMemo(() => detalle?.secciones || {}, [detalle]);
    const { conRegistro, sinRegistro: sinRegistroTodas } = useMemo(() => particionarSecciones(secciones), [secciones]);
    // AUD-EDIT-02: completar una sección AUSENTE (p.ej. signos_vitales) exige
    // una superficie donde escribir -- antes de esto, `edicion.seccion` podía
    // apuntar a una sección de `sinRegistro`, pero ninguna tarjeta se
    // renderizaba para ella (el bloque "Sin registro" es sólo informativo +
    // el botón "Completar"). Mientras se completa, esa sección sale de la
    // lista informativa y se muestra como una tarjeta más, igual que
    // cualquier otra en edición.
    const seccionSiendoCompletada = useMemo(
        () => (edicion ? sinRegistroTodas.find((s) => s.id === edicion.seccion) || null : null),
        [edicion, sinRegistroTodas]
    );
    const sinRegistro = useMemo(
        () => (seccionSiendoCompletada ? sinRegistroTodas.filter((s) => s.id !== seccionSiendoCompletada.id) : sinRegistroTodas),
        [sinRegistroTodas, seccionSiendoCompletada]
    );
    const corregidas = useMemo(() => seccionesCorregidas(historial), [historial]);
    const progreso = useMemo(() => resumenProgreso(secciones, historial), [secciones, historial]);
    const cambios = useMemo(() => cambiosRecientes(historial, 4), [historial]);
    const totalCambios = useMemo(() => cambiosRecientes(historial, null).length, [historial]);

    // Primera sección con registro como activa por defecto.
    useEffect(() => {
        if (!seccionActiva && conRegistro.length > 0) setSeccionActiva(conRegistro[0].id);
    }, [conRegistro, seccionActiva]);

    const estadoAuditoria = detalle?.estado_auditoria?.estado || "PENDIENTE";
    const yaAuditada = estadoAuditoria === "AUDITADA";
    const citaFinalizada = Number(detalle?.estado_cita?.idestado_cita) === CITA_ESTADO_FINALIZADA;
    const tienePermisoCorregir = hasPermission("auditoria.clinica.correct");
    const puedeAccionar = tienePermisoCorregir && citaFinalizada;
    // Corregir exige además que la revisión no esté cerrada: AUDITADA es
    // cierre y el backend responde 409 (esto sólo evita ofrecer una acción
    // que iba a fallar).
    // ELEGIBILIDAD-53: hay atenciones con prueba POSITIVA de que nunca hubo
    // consulta (citas que el cron llevó a Finalizada sin que el paciente
    // llegara). Ahí no se ofrece corregir -- el backend además responde 409 --
    // pero SÍ se puede leer la atención y cerrar la revisión.
    const atencionCorregible = puedeCorregirse(detalle);
    const chipElegible = chipElegibilidad(detalle);
    const puedeCorregir = puedeAccionar && !yaAuditada && atencionCorregible;

    // -- FASE 2: corrección en sitio ---------------------------------------
    const abrirCorreccion = (idSeccion) => {
        setErrorEdicion(null);
        setEdicion(iniciarEdicion(idSeccion, secciones));
        setSeccionActiva(idSeccion);
    };

    const cambiarEdicion = (patch) =>
        setEdicion((e) => (e ? { ...e, ...patch, valores: { ...e.valores, ...(patch.valores || {}) } } : e));

    const cambiarRazon = (clave, patch) =>
        setEdicion((e) =>
            e ? { ...e, razones: { ...e.razones, [clave]: { ...(e.razones?.[clave] || {}), ...patch } } } : e
        );

    const cancelarEdicion = () => {
        // Cancelar con cambios sin guardar pide confirmación: lo escrito se
        // pierde y eso no puede pasar por accidente.
        if (hayCambioReal(edicion, secciones)) {
            // eslint-disable-next-line no-alert
            const confirmado = window.confirm("Hay una corrección sin guardar. ¿Descartar los cambios?");
            if (!confirmado) return;
        }
        setEdicion(null);
        setErrorEdicion(null);
    };

    const guardarCorreccion = async () => {
        if (!edicion || motivoNoGuardable(edicion, secciones)) return;
        setGuardando(true);
        setErrorEdicion(null);
        // Una corrección por SECCIÓN, nunca un lote de secciones distintas.
        const res = await PostAuditoriaClinicaCorreccion(idcita, { changes: construirCambios(edicion, secciones) });
        setGuardando(false);
        if (res?.success) {
            message.success("Corrección registrada");
            setEdicion(null);
            await cargar();
        } else {
            // El error se muestra en la propia tarjeta y NO se pierde nada de
            // lo escrito (criterio de aceptación 8).
            setErrorEdicion(res?.message || "No se pudo guardar la corrección.");
        }
    };

    // AUD-EDIT-QA-03c: para cerrar hace falta comentario general. Cuenta el que
    // ya esté guardado desde el panel (no se obliga a repetirlo) o el que se
    // escriba en el propio diálogo de cierre. Es la misma regla que valida el
    // backend en `MarcarAuditadaCita`; aquí sólo se evita ofrecer una acción
    // que iba a ser rechazada.
    const hayComentarioGuardado = !!(comentarioActual?.comentario || "").trim();
    const comentarioParaCerrar = hayComentarioGuardado || comentarioCierre.trim().length > 0;

    // AUD-EDIT-01: guarda una versión nueva del comentario general. Nunca se
    // bloquea por `yaAuditada` -- a propósito, ver FASE 12 del ticket.
    const guardarComentario = async (texto) => {
        setGuardandoComentario(true);
        setErrorComentario(null);
        const res = await PostAuditoriaClinicaComentario(idcita, texto);
        setGuardandoComentario(false);
        if (res?.success) {
            setComentarioActual(res.data);
            setComentarioHistorial((h) => [res.data, ...h]);
            message.success("Comentario guardado");
            return true;
        }
        setErrorComentario(res?.message || "No se pudo guardar el comentario.");
        return false;
    };

    // `Esc` cancela la edición en curso (criterio de aceptación 5). Se
    // registra sólo mientras hay una corrección abierta.
    useEffect(() => {
        if (!edicion) return undefined;
        const alPulsar = (e) => {
            if (e.key === "Escape") cancelarEdicion();
        };
        document.addEventListener("keydown", alPulsar);
        return () => document.removeEventListener("keydown", alPulsar);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edicion, secciones]);

    const irASeccion = (idSeccion) => {
        setSeccionActiva(idSeccion);
        const nodo = document.getElementById(`ac-seccion-${idSeccion}`);
        if (nodo && typeof nodo.scrollIntoView === "function") {
            nodo.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const volverALista = () => {
        navigate(contexto.retorno?.ruta || RUTA_LISTA_AUDITORIA, {
            state: contexto.retorno ? { ...contexto.retorno } : undefined,
        });
    };

    const irAAtencion = (indice) => {
        if (!cola || indice < 0 || indice >= cola.ids.length) return;
        navigate(`/auditoria-clinica/${cola.ids[indice]}`, {
            state: { ...contexto, fila: cola.filas?.[indice] ?? null, cola: { ...cola, indice } },
        });
    };

    const indiceCola = cola ? cola.indice ?? 0 : -1;
    const haySiguiente = !!cola && indiceCola + 1 < cola.ids.length;

    const ejecutar = async (fn, exito) => {
        setEjecutando(true);
        const res = await fn();
        setEjecutando(false);
        if (res?.success) {
            message.success(exito);
        } else {
            message.error(res?.message || "No se pudo actualizar el estado de la auditoría.");
        }
        // Se relee siempre: si falló por 409 el estado real ya cambió y la
        // pantalla debe reflejarlo en vez de quedar desincronizada.
        await cargar();
        return res?.success === true;
    };

    const guardarYContinuarDespues = async () => {
        // "Continuar después" = dejar la revisión abierta (EN_PROCESO) y
        // volver a la lista. Si ya estaba EN_PROCESO el endpoint es
        // idempotente (no reinicia started_at/started_by).
        if (estadoAuditoria === "PENDIENTE") {
            await ejecutar(() => PostIniciarAuditoria(idcita), "Revisión guardada como «en proceso»");
        }
        volverALista();
    };

    const marcarAuditada = async () => {
        setConfirmarCierre(false);
        const ok = await ejecutar(
            () => PostMarcarAuditada(idcita, estadoAuditoria, comentarioCierre),
            "Atención marcada como auditada"
        );
        if (ok) setComentarioCierre("");
        if (ok && haySiguiente) irAAtencion(indiceCola + 1);
    };

    // Motivo EXPLÍCITO por el que no se puede cerrar la auditoría. Nunca un
    // botón deshabilitado sin explicación.
    let bloqueo = null;
    if (edicion) {
        bloqueo = `Termina o cancela la corrección de «${SECCION_LABEL[edicion.seccion] || edicion.seccion}» antes de cerrar la auditoría.`;
    }
    if (!citaFinalizada) {
        bloqueo = `Solo se pueden auditar atenciones finalizadas; esta cita está en estado «${detalle?.estado_cita?.estado || "desconocido"}».`;
    }
    let nota = yaAuditada
        ? "Auditoría cerrada. Esta atención ya no admite correcciones."
        : "Puedes cerrar la auditoría aunque no hayas registrado ninguna corrección.";
    if (!yaAuditada && !atencionCorregible) {
        nota = "Sin correcciones posibles: puedes revisarla y cerrarla como auditada.";
    }

    if (cargando) return <AtencionSkeleton />;

    if (error) {
        return (
            <div className="page-content">
                <div className="container-fluid">
                    <Alert type="error" showIcon message={error} data-testid="ac-error" className="mb-3" />
                    <Button onClick={volverALista}>Volver a la lista de auditoría</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content ac-page ac-atencion" ref={contenedorRef} data-testid="ac-atencion">
            <AtencionBarraSuperior
                idcita={idcita}
                fecha={detalle?.fecha}
                estadoAuditoria={estadoAuditoria}
                estadoCita={detalle?.estado_cita}
                chipElegibilidad={chipElegible}
                correcciones={historial.length}
                onVolver={volverALista}
                onVerHistorial={() => {
                    setHistorialKey((k) => k + 1);
                    setHistorialAbierto(true);
                }}
                cola={
                    cola
                        ? {
                              indice: indiceCola,
                              total: cola.ids.length,
                              tieneAnterior: indiceCola > 0,
                              tieneSiguiente: haySiguiente,
                              onAnterior: () => irAAtencion(indiceCola - 1),
                              onSiguiente: () => irAAtencion(indiceCola + 1),
                          }
                        : null
                }
            />

            <AtencionIdentificacion detalle={detalle} fila={contexto.fila} />

            <div className="ac-cuerpo">
                <div className="ac-col-izq">
                    <AtencionIndiceSecciones
                        conRegistro={conRegistro}
                        sinRegistro={sinRegistro}
                        progreso={progreso}
                        seccionActiva={seccionActiva}
                        corregidas={corregidas}
                        onIrASeccion={irASeccion}
                    />
                </div>

                <div className="ac-col-centro">
                    {!atencionCorregible && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Atención no corregible"
                            description={MOTIVO_NO_AUDITABLE}
                            data-testid="ac-no-corregible"
                        />
                    )}

                    <div className="ac-zona">
                        <span>Con registro clínico · {conRegistro.length}</span>
                        <span className="ac-zona__linea" />
                    </div>

                    {conRegistro.length === 0 ? (
                        <Alert
                            type="warning"
                            showIcon
                            message="Esta atención no tiene ninguna sección clínica registrada."
                            data-testid="ac-sin-secciones"
                        />
                    ) : (
                        conRegistro.map((seccion) => {
                            const editandoEsta = edicion?.seccion === seccion.id;
                            return (
                                <AtencionSeccionCard
                                    key={seccion.id}
                                    seccion={seccion}
                                    secciones={secciones}
                                    correccion={ultimaCorreccionDeSeccion(historial, seccion.id)}
                                    editando={editandoEsta}
                                    // Una sola sección en edición a la vez: mientras hay
                                    // una abierta, las demás no ofrecen "Corregir".
                                    onCorregir={puedeCorregir && !edicion ? abrirCorreccion : undefined}
                                    onVerCambio={() => {
                                        setHistorialKey((k) => k + 1);
                                        setHistorialAbierto(true);
                                    }}
                                    pie={
                                        editandoEsta ? (
                                            <AtencionSeccionPie
                                                motivoNoGuardable={motivoNoGuardable(edicion, secciones)}
                                                guardando={guardando}
                                                onCancelar={cancelarEdicion}
                                                onGuardar={guardarCorreccion}
                                            />
                                        ) : null
                                    }
                                >
                                    {editandoEsta ? (
                                        <AtencionSeccionEdicion
                                            edicion={edicion}
                                            secciones={secciones}
                                            onCambio={cambiarEdicion}
                                            onRazon={cambiarRazon}
                                            error={errorEdicion}
                                        />
                                    ) : null}
                                </AtencionSeccionCard>
                            );
                        })
                    )}

                    {seccionSiendoCompletada && (
                        <AtencionSeccionCard
                            key={seccionSiendoCompletada.id}
                            seccion={seccionSiendoCompletada}
                            secciones={secciones}
                            correccion={null}
                            editando
                            pie={
                                <AtencionSeccionPie
                                    motivoNoGuardable={motivoNoGuardable(edicion, secciones)}
                                    guardando={guardando}
                                    onCancelar={cancelarEdicion}
                                    onGuardar={guardarCorreccion}
                                />
                            }
                        >
                            <AtencionSeccionEdicion
                                edicion={edicion}
                                secciones={secciones}
                                onCambio={cambiarEdicion}
                                onRazon={cambiarRazon}
                                error={errorEdicion}
                            />
                        </AtencionSeccionCard>
                    )}

                    {sinRegistro.length > 0 && (
                        <>
                            <div className="ac-zona">
                                <span>Sin registro clínico · {sinRegistro.length}</span>
                                <span className="ac-zona__linea" />
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setOcultarSinRegistro((v) => !v)}
                                    data-testid="ac-toggle-sinregistro"
                                >
                                    {ocultarSinRegistro ? "Mostrar" : "Ocultar"}
                                </Button>
                            </div>
                            {!ocultarSinRegistro && (
                                <AtencionSinRegistroBloque
                                    secciones={sinRegistro}
                                    // Misma puerta que "Corregir": si no puede
                                    // corregir, tampoco completar.
                                    onCompletar={puedeCorregir && !edicion ? abrirCorreccion : undefined}
                                />
                            )}
                        </>
                    )}

                    {/* El comentario general cierra la columna central: es el
                        último paso natural del flujo (revisar las secciones ->
                        opinar sobre la atención entera -> cerrar) y necesita
                        ancho de verdad, porque un comentario de auditoría real
                        ocupa varios párrafos. Antes vivía en el raíl derecho de
                        300px, donde escribirlo era incómodo. */}
                    <AtencionComentarioGeneral
                        actual={comentarioActual}
                        historial={comentarioHistorial}
                        puedeEditar={puedeAccionar}
                        guardando={guardandoComentario}
                        error={errorComentario}
                        onGuardar={guardarComentario}
                        obligatorioParaCerrar={!yaAuditada}
                    />
                </div>

                <div className="ac-col-der">
                    <AtencionPanelCorrecciones
                        cambios={cambios}
                        total={totalCambios}
                        onVerHistorial={() => {
                            setHistorialKey((k) => k + 1);
                            setHistorialAbierto(true);
                        }}
                    />
                </div>
            </div>

            <AtencionBarraAcciones
                puedeCorregir={puedeAccionar}
                bloqueo={bloqueo}
                nota={nota}
                ejecutando={ejecutando}
                mostrarGuardar={!yaAuditada}
                mostrarCerrar={!yaAuditada}
                onGuardarContinuar={guardarYContinuarDespues}
                onMarcarAuditada={() => setConfirmarCierre(true)}
                onSiguiente={() => irAAtencion(indiceCola + 1)}
                haySiguiente={haySiguiente}
            />

            <Modal
                abierto={confirmarCierre}
                titulo="Cerrar la auditoría"
                ancho="confirmacion"
                onCerrar={() => setConfirmarCierre(false)}
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: "Marcar como auditada",
                    onClick: marcarAuditada,
                    cargando: ejecutando,
                    // AUD-EDIT-QA-03c: el comentario general es obligatorio para
                    // cerrar. Vale el que ya esté guardado desde el panel; si no
                    // hay ninguno, hay que escribirlo aquí mismo.
                    disabled: !comentarioParaCerrar,
                }}
            >
                <p>
                    La atención #{idcita} · {detalle?.paciente?.nombres_completos || "paciente sin determinar"} quedará marcada
                    como auditada. Después de esto ya no podrás corregir secciones.
                </p>
                {/* Sólo datos REALES: no hay checklist de verificación que
                    resumir (no existe dónde persistirlo, ver FASE 0). */}
                <ResumenConsecuencias
                    filas={[
                        {
                            etiqueta: "Correcciones registradas",
                            valor: totalCambios,
                            tono: totalCambios > 0 ? "ok" : "neutro",
                        },
                        { etiqueta: "Secciones con registro clínico", valor: conRegistro.length, tono: "ok" },
                        {
                            etiqueta: "Secciones sin registro clínico",
                            valor: sinRegistro.length,
                            tono: sinRegistro.length > 0 ? "aviso" : "ok",
                        },
                    ]}
                />

                {/* El comentario de la auditoría en su conjunto. Es OBLIGATORIO
                    para cerrar: cerrar es afirmar que se revisó la atención, y
                    sin una palabra no queda constancia de qué se revisó. Si ya
                    se guardó desde el panel, aquí sólo se recuerda -- no se
                    obliga a escribirlo dos veces. */}
                {hayComentarioGuardado ? (
                    <p className="ac-cierre__comentario-ok" data-testid="ac-cierre-comentario-ok">
                        Se cerrará con el comentario general ya guardado. Si escribes algo aquí, se guardará como una versión
                        nueva.
                    </p>
                ) : null}
                <label className="ac-cierre__comentario">
                    <span className="ac-etiqueta">
                        Comentario de la auditoría {hayComentarioGuardado ? "(opcional: ya hay uno guardado)" : "*"}
                    </span>
                    <textarea
                        rows={6}
                        value={comentarioCierre}
                        maxLength={COMENTARIO_MAX}
                        placeholder="Por ejemplo: «En la historia clínica del paciente … no se encontraron observaciones. Historia clínica se relaciona correctamente con diagnósticos CIE10.»"
                        onChange={(e) => setComentarioCierre(e.target.value)}
                        data-testid="ac-comentario-cierre"
                    />
                    <span className="ac-contador">
                        {comentarioCierre.trim().length} / {COMENTARIO_MAX}
                    </span>
                    {!comentarioParaCerrar && (
                        <span className="ac-cierre__comentario-falta" data-testid="ac-cierre-falta-comentario">
                            Escribe el comentario general para poder marcar la atención como auditada.
                        </span>
                    )}
                </label>
            </Modal>

            <Drawer
                title={`Historial de correcciones · Atención #${idcita}`}
                width={640}
                open={historialAbierto}
                onClose={() => setHistorialAbierto(false)}
                destroyOnClose
            >
                <AuditoriaHistorialCorrecciones idcita={idcita} key={historialKey} />
            </Drawer>
        </div>
    );
}

export default AuditoriaAtencionDetalle;
